import React, { useMemo, useCallback, useState } from 'react';
import MakePayment from '../MakePayment';
import { PaymentConfirmStep, PaymentConfirmationStep, PaymentInformationStep } from '../steps/Steps';
import TermsModal from './TermsModal';
import PolicyVerification from './verification/PolicyVerification';
import numeral from 'numeral';

const OneTimePayment = (props) => {
	const { payment, paymentActions, onClose, onVerification } = props;

	const [verificationData, setVerificationData] = useState({});
	const [termsOpen, setTermsOpen] = useState(false);
	const [policy, setPolicy] = useState();
	const [error, setError] = useState();

	const policies = useMemo(() => (policy ? [policy] : null), [policy]);

	const onShowTerms = useCallback(() => {
		setTermsOpen(true);
	}, []);

	const onCloseTerms = useCallback(() => {
		setTermsOpen(false);
	}, []);

	const onOneTimeChange = useCallback(
		(e, data) => {
			setVerificationData({ ...verificationData, [data.name]: data.name === 'agreeTerms' ? data.checked : data.value });
		},
		[verificationData]
	);

	const onStepChange = useCallback(() => {
		setError(null);
	}, []);

	const setupForPolicies = useCallback(
		(policies, isFuture = false) => {
			var [policy] = policies;
			if (policies.some((p) => p.billing.isPartOfAccountBill)) {
				policy = policies.find((p) => p.billing.isPrimaryAccountPolicy);
			}

			const { isPartOfAccountBill, accountOutstandingBalance, accountPayInFull, accountBillId, isPrimaryAccountPolicy, accountNumber } = policy.billing;
			const accountPolicies = policies.filter((i) => i.billing.isPartOfAccountBill && !i.billing.isPrimaryAccountPolicy && i.billing.accountNumber === accountNumber).map((i) => i.policyNumber);

			var displayName = isPartOfAccountBill ? policy.billing.accountNumber : policy.policyNumber;

			var payInFullAmount = isPartOfAccountBill ? accountPayInFull : policy.billing.payInFullAmount;
			var outstandingBalance = isPartOfAccountBill ? policy.billing.accountOutstandingBalance : policy.billing.outstandingBalance;
			var paymentOption = outstandingBalance >= 1 ? 'Current Balance' : payInFullAmount > 0 ? 'Total Balance' : 'Custom Payment';
			var isPayable = outstandingBalance >= 1 || payInFullAmount > 0;
			var amount = outstandingBalance >= 1 ? outstandingBalance : payInFullAmount > 0 ? payInFullAmount : 0;

			if (amount === 0 && policy.futurePolicy) {
				return setupForPolicies([policy.futurePolicy], true);;
			}

			if (amount > payment.settings.maximumVendorPaymentAmount) {
				setError(`Payments greater than ${numeral(payment.settings.maximumVendorPaymentAmount).format('$0,0.00')} cannot be made online.  Please contact customer service at 800.477.1660.`);
				return false;
			}

			const paymentInfo = {
				displayName: `${displayName}${isFuture ? ' (Renewal)' : ''}`,
				imageNumber: policy.policyImageNum,
				policyHolder: policy.policyHolder,
				policyNumber: policy.policyNumber,
				isPayable,
				paymentOption,
				outstandingBalance,
				payInFullAmount,
				amount,
				isPartOfAccountBill,
				isPrimaryAccountPolicy,
				accountOutstandingBalance,
				accountPayInFull,
				accountBillId,
				accountNumber,
				accountPolicies,
				...policy,
			};

			setPolicy(paymentInfo);
			onVerification(paymentInfo);
			return true;
		},
		[onVerification, payment.settings]
	);

	const getPolicy = useCallback(async () => {
		const { policyNumber, accountNumber } = verificationData;

		try {
			const { response } = await paymentActions.getOnetimePolicies([{ policyNumber, nickname: 'Nickname' }], accountNumber);
			const { policies } = response;
			return setupForPolicies(policies);
		} catch (error) {
			console.log(error);
		}
	}, [paymentActions, setupForPolicies, verificationData]);

	const policyVerificationStep = useCallback(
		({ setStep, step, loading, setLoading }) => {
			const _step = {
				subheader: 'Verify account/policy information',
				onContinue: async () => {
					setLoading(true);
					const message = "The information entered doesn't match our records. Please verify the information. If it is correct, please contact customer service at 800.477.1660.";
					try {
						const { policyNumber, paymentNumber, policyHolder, zip, accountNumber } = verificationData;
						if (!!paymentNumber) {
							const { response } = await paymentActions.verifyPolicyByPaymentNumber({ policyNumber, paymentNumber });
							if (response.isValid) {
								if (await getPolicy()) {
									setStep(step + 1);
								}
							} else setError(message);
						} else if (!!accountNumber) {
							const { response } = await paymentActions.verifyAccountNumber({ accountNumber, policyHolder, zip });
							if (response.isValid) {
								if (await getPolicy()) {
									setStep(step + 1);
								}
							} else setError(message);
						} else {
							const { response } = await paymentActions.verifyOnetimePolicy({ policyNumber, policyHolder, zip });
							if (response.isValid) {
								if (await getPolicy()) {
									setStep(step + 1);
								}
							} else setError(message);
						}
					} catch {
						setError(message);
					}

					setLoading(false);
				},
			};

			_step.content = <PolicyVerification data={verificationData} onChange={onOneTimeChange} onShowTerms={onShowTerms} loading={loading} />;
			_step.getErrors = () => {
				const errors = [];
				if (!!verificationData.paymentNumber) {
					if (!(!!verificationData.policyNumber && !!verificationData.paymentNumber && !!verificationData.agreeTerms)) errors.push('Required Data');
				} else if (!!verificationData.accountNumber) {
					if (!(!!verificationData.policyHolder && !!verificationData.zip && !!verificationData.agreeTerms)) errors.push('Required Data');
				} else {
					if (!(!!verificationData.policyNumber && !!verificationData.policyHolder && !!verificationData.zip && !!verificationData.agreeTerms)) errors.push('Required Data');
				}
				return errors;
			};
			return _step;
		},
		[getPolicy, onOneTimeChange, onShowTerms, paymentActions, verificationData]
	);

	const steps = useMemo(() => [policyVerificationStep, PaymentInformationStep, PaymentConfirmStep, PaymentConfirmationStep], [policyVerificationStep]);

	const onReset = useCallback(() => {
		setTermsOpen(false);
		setVerificationData({});
	}, []);

	return (
		<React.Fragment>
			<TermsModal open={termsOpen} onClose={onCloseTerms} />
			<MakePayment {...props} policies={policies} steps={steps} onReset={onReset} inline error={error} onStepChange={onStepChange} onClose={onClose} hidePaymentOption walletsDisabled isOneTime />
		</React.Fragment>
	);
};

export default OneTimePayment;
